<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="store.loading" />
  <div v-else class="builder-page">
    <div class="form-page-header">
      <div class="form-page-header__icon"><i class="pi pi-mobile"></i></div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إدارة الصفحة الرئيسية للموبايل</h1>
        <p class="form-page-header__subtitle">تحكم كامل في محتوى وترتيب أقسام الصفحة الرئيسية مع معاينة فورية</p>
      </div>
      <div class="builder-header__actions">
        <span v-if="dirty" class="unsaved-badge"><i class="pi pi-circle-fill"></i> تغييرات غير محفوظة</span>
        <a :href="publicHomeUrl" target="_blank" class="btn-cancel"><i class="pi pi-external-link"></i> معاينة الموقع</a>
      </div>
    </div>

    <div class="builder-layout">
      <!-- Preview (visually left) -->
      <div class="builder-preview">
        <div class="device-toggle">
          <button :class="{ active: device === 'mobile' }" @click="device = 'mobile'"><i class="pi pi-mobile"></i> موبايل</button>
          <button :class="{ active: device === 'desktop' }" @click="device = 'desktop'"><i class="pi pi-desktop"></i> ديسكتوب</button>
        </div>
        <PreviewFrame :device="device" :sections="store.sections" :selected-key="selectedSection?.key || null" @section-click="onPreviewSectionClick" />
      </div>

      <!-- Sections panel (visually right) -->
      <div class="builder-panel">
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-list"></i></div>
            <span class="form-card__title">أقسام الصفحة</span>
            <button type="button" class="add-section-btn" @click="showAddDialog = true">
              <i class="pi pi-plus"></i> إضافة قسم
            </button>
          </div>
          <div class="form-card__body">
            <div v-if="!store.sections.length" class="empty-state">
              <i class="pi pi-inbox empty-state__icon"></i>
              <p>لا توجد أقسام بعد</p>
              <button type="button" class="btn-submit" :disabled="store.loading" @click="onRestoreDefaults">
                <i class="pi pi-refresh"></i> إضافة الأقسام الافتراضية
              </button>
            </div>
            <draggable v-else v-model="store.sections" item-key="id" handle=".section-drag-handle" @end="onReorder">
              <template #item="{ element: section }">
                <div class="section-row" :class="{ 'section-row--selected': selectedId === section.id, 'section-row--inactive': !section.is_active }">
                  <i class="pi pi-bars section-drag-handle"></i>
                  <span class="section-row__name">{{ sectionLabel(section) }}</span>
                  <InputSwitch :model-value="section.is_active" @update:model-value="() => toggleActive(section)" />
                  <button type="button" class="section-edit-btn" @click="selectSection(section)">
                    <i class="pi pi-pencil"></i> تعديل
                  </button>
                </div>
              </template>
            </draggable>
          </div>
        </div>

        <div class="form-card" v-if="selectedSection">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-pencil"></i></div>
            <span class="form-card__title">تعديل: {{ sectionLabel(selectedSection) }}</span>
          </div>
          <div class="form-card__body">
            <component
              :is="editorComponent"
              v-model="editingSettings"
              :current-logo-link="selectedSection.settings?.logo ? absoluteUrl(selectedSection.settings.logo) : null"
              :current-avatar-link="selectedSection.settings?.teacher_avatar ? absoluteUrl(selectedSection.settings.teacher_avatar) : null"
              @update:logoFile="(f) => (pendingLogoFile = f)"
              @update:avatarFile="(f) => (pendingAvatarFile = f)"
            />
            <div class="editor-actions">
              <button type="button" class="btn-submit" :disabled="store.saving" @click="saveSelected">
                <i class="pi pi-save"></i> {{ store.saving ? 'جارٍ الحفظ...' : 'حفظ' }}
              </button>
              <button type="button" class="btn-cancel" @click="cancelEditing">إلغاء</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddSectionDialog v-model:visible="showAddDialog" @created="onSectionCreated" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import draggable from 'vuedraggable';
import axios from 'axios';
import Swal from 'sweetalert2';
import InputSwitch from 'primevue/inputswitch';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useHomepageStore } from '@/stores/useHomepageStore';
import PreviewFrame from '@/components/homepage/PreviewFrame.vue';
import AddSectionDialog from '@/components/homepage/AddSectionDialog.vue';

import HeaderEditor from './editors/HeaderEditor.vue';
import HeroEditor from './editors/HeroEditor.vue';
import QuickAccessEditor from './editors/QuickAccessEditor.vue';
import AcademicSystemsEditor from './editors/AcademicSystemsEditor.vue';
import HomeProductsEditor from './editors/HomeProductsEditor.vue';
import StatisticsEditor from './editors/StatisticsEditor.vue';
import TestimonialsEditor from './editors/TestimonialsEditor.vue';
import CtaEditor from './editors/CtaEditor.vue';
import FooterEditor from './editors/FooterEditor.vue';

const breadcrumbs = [
  { name: 'الرئيسية', path: '/' },
  { name: 'إدارة الصفحة الرئيسية للموبايل', path: '/homepage-builder' },
];

const sectionLabels = {
  header: 'الهيدر',
  hero: 'الترحيب / Hero',
  quick_access: 'الوصول السريع',
  academic_systems: 'الأنظمة الدراسية',
  home_products: 'المنتجات (سنوات/كورسات)',
  statistics: 'الإحصائيات',
  testimonials: 'آراء الطلاب',
  cta: 'دعوة للتسجيل',
  footer: 'الفوتر',
};

// home_products covers two distinct instances (academic years / courses) that
// would otherwise show the same generic label in the sections list.
function sectionLabel(section) {
  if (section.type === 'home_products') {
    return section.settings?.kind === 'academic_year' ? 'السنوات الدراسية' : 'الكورسات';
  }
  return sectionLabels[section.type] || section.type;
}

const editorComponents = {
  header: HeaderEditor,
  hero: HeroEditor,
  quick_access: QuickAccessEditor,
  academic_systems: AcademicSystemsEditor,
  home_products: HomeProductsEditor,
  statistics: StatisticsEditor,
  testimonials: TestimonialsEditor,
  cta: CtaEditor,
  footer: FooterEditor,
};

const store = useHomepageStore();
const selectedId = ref(null);
const editingSettings = ref({});
const pendingLogoFile = ref(null);
const pendingAvatarFile = ref(null);
const dirty = ref(false);
const device = ref('mobile');
const showAddDialog = ref(false);

const publicHomeUrl = localStorage.getItem('homepage_preview_origin') || 'https://mobile.moustafahosney.com';

const selectedSection = computed(() => store.sections.find((s) => s.id === selectedId.value) || null);
const editorComponent = computed(() => (selectedSection.value ? editorComponents[selectedSection.value.type] : null));

function absoluteUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  // axios.defaults.baseURL is "<api-origin>/api" (see main.js) — strip the
  // "/api" suffix to get the origin that serves uploaded image paths.
  const apiBase = axios.defaults.baseURL || '';
  const origin = apiBase.replace(/\/api\/?$/, '');
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

function selectSection(section) {
  if (dirty.value && !window.confirm('لديك تعديلات غير محفوظة في القسم الحالي، هل تريد المتابعة بدون حفظها؟')) {
    return;
  }
  selectedId.value = section.id;
  editingSettings.value = JSON.parse(JSON.stringify(section.settings || {}));
  pendingLogoFile.value = null;
  pendingAvatarFile.value = null;
  dirty.value = false;
}

function onPreviewSectionClick(key) {
  const section = store.sections.find((s) => s.key === key);
  if (section) selectSection(section);
}

// Smart add flow: right after a section is created, jump straight into its
// editor instead of leaving the teacher to find it in the list themselves.
function onSectionCreated(section) {
  if (section) selectSection(section);
}

async function onRestoreDefaults() {
  try {
    await store.restoreDefaults();
  } catch {
    Swal.fire({ icon: 'error', title: 'تعذر إضافة الأقسام الافتراضية' });
  }
}

async function toggleActive(section) {
  try {
    await store.toggleActive(section.id);
    dirty.value = false;
  } catch {
    Swal.fire({ icon: 'error', title: 'تعذر تغيير حالة الظهور' });
  }
}

async function onReorder() {
  try {
    await store.reorder(store.sections.map((s) => s.id));
  } catch {
    Swal.fire({ icon: 'error', title: 'تعذر حفظ الترتيب' });
    await store.fetchSections();
  }
}

async function saveSelected() {
  if (!selectedSection.value) return;
  try {
    const hasFile = pendingLogoFile.value || pendingAvatarFile.value;
    let payload;
    if (hasFile) {
      payload = new FormData();
      payload.append('settings', JSON.stringify(editingSettings.value));
      if (pendingLogoFile.value) payload.append('logo_file', pendingLogoFile.value);
      if (pendingAvatarFile.value) payload.append('avatar_file', pendingAvatarFile.value);
    } else {
      payload = { settings: editingSettings.value };
    }
    await store.updateSection(selectedSection.value.id, payload);
    dirty.value = false;
    pendingLogoFile.value = null;
    pendingAvatarFile.value = null;
    Swal.fire({ icon: 'success', title: 'تم الحفظ بنجاح', timer: 1500, showConfirmButton: false });
  } catch (e) {
    const errors = e?.response?.data?.errors;
    const message = errors
      ? Object.values(errors).flat().join('\n')
      : (e?.response?.data?.message || 'تعذر حفظ التعديلات');
    Swal.fire({ icon: 'error', title: 'تعذر الحفظ', text: message });
  }
}

function cancelEditing() {
  if (dirty.value && !window.confirm('لديك تعديلات غير محفوظة، هل تريد تجاهلها؟')) return;
  selectedId.value = null;
  dirty.value = false;
}

function onBeforeUnload(e) {
  if (!dirty.value) return;
  e.preventDefault();
  e.returnValue = '';
}

watch(editingSettings, () => { dirty.value = true; }, { deep: true });

onMounted(() => {
  store.fetchSections();
  window.addEventListener('beforeunload', onBeforeUnload);
});
onBeforeUnmount(() => window.removeEventListener('beforeunload', onBeforeUnload));
</script>

<style scoped>
.builder-header__actions { display: flex; align-items: center; gap: .75rem; margin-right: auto; }
.unsaved-badge { color: #f59e0b; font-size: .82rem; display: flex; align-items: center; gap: .35rem; }
.unsaved-badge .pi { font-size: .5rem; }

.builder-layout { display: flex; direction: ltr; gap: 1.5rem; align-items: flex-start; }
.builder-preview { direction: ltr; position: sticky; top: 1rem; flex-shrink: 0; }
.builder-panel { direction: rtl; flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1.25rem; }

.device-toggle { display: flex; gap: .5rem; margin-bottom: .75rem; direction: rtl; }
.device-toggle button {
  border: 1px solid var(--border, #e5e7eb); background: #fff; border-radius: 8px;
  padding: .4rem .9rem; font-size: .85rem; cursor: pointer; display: flex; align-items: center; gap: .4rem;
}
.device-toggle button.active { background: var(--primary, #051629); color: #fff; border-color: var(--primary, #051629); }

.section-row {
  display: flex; align-items: center; gap: .75rem;
  padding: .65rem .75rem; border-radius: 10px; border: 1px solid transparent;
  background: #fff; margin-bottom: .5rem;
}
.section-row--selected { border-color: var(--primary, #051629); background: rgba(5,22,41,.04); }
.section-row--inactive { opacity: .55; }
.section-drag-handle { cursor: grab; color: #9ca3af; }
.section-row__name { flex: 1; font-weight: 600; }
.section-edit-btn {
  border: none; background: rgba(5,22,41,.06); border-radius: 8px; padding: .35rem .75rem;
  font-size: .82rem; cursor: pointer; display: flex; align-items: center; gap: .35rem;
}

.editor-actions { display: flex; gap: .75rem; margin-top: 1.25rem; }

.add-section-btn {
  margin-inline-start: auto;
  display: flex; align-items: center; gap: .4rem;
  border: none; background: var(--primary, #051629); color: #fff;
  border-radius: 8px; padding: .5rem 1rem; font-size: .85rem; font-weight: 600; cursor: pointer;
}
.add-section-btn:hover { opacity: .92; }

.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: .75rem; padding: 2.5rem 1rem; text-align: center; color: #6b7280;
}
.empty-state__icon { font-size: 2.25rem; color: #9ca3af; }
</style>
